import { useState } from 'react';
import { Mail } from 'lucide-react';
import { useI18n, useNamespace } from '../context/I18nContext';
import { useTenant } from '../context/TenantContext';
import { apiClient } from '../shared/lib/apiClient';
import { navigate } from '../shared/lib/router';

export default function PasswordResetRequestPage() {
  useNamespace('common');
  const { t } = useI18n();
  const { tenantSlug: defaultTenantSlug } = useTenant();

  const [tenantSlug, setTenantSlug] = useState(defaultTenantSlug || '');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // The backend always returns success here regardless of whether the
      // email exists, to avoid user enumeration - so we always show the
      // generic success message too.
      await apiClient.post('/auth/password-reset/request', { tenantSlug, email });
      setSubmitted(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h1 className="auth-card__title">{t('common', 'appName')}</h1>
        <p className="auth-card__subtitle">{t('common', 'passwordReset.requestTitle')}</p>

        {submitted ? (
          <>
            <p className="success-banner">{t('common', 'passwordReset.successMessage')}</p>
            <button type="button" className="btn-link" onClick={() => navigate('/')}>
              {t('common', 'passwordReset.backToLogin')}
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <p className="auth-card__hint">{t('common', 'passwordReset.requestInstructions')}</p>

            {error && <div className="error-banner">{t('common', 'errorBanner')}</div>}

            <div className="form-field">
              <label className="form-label" htmlFor="reset-tenant">
                {t('common', 'passwordReset.tenantLabel')}
              </label>
              <input
                id="reset-tenant"
                className="form-input"
                type="text"
                value={tenantSlug}
                onChange={(event) => setTenantSlug(event.target.value)}
                required
              />
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="reset-email">
                {t('common', 'passwordReset.emailLabel')}
              </label>
              <input
                id="reset-email"
                className="form-input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              <Mail size={16} /> {t('common', 'passwordReset.submit')}
            </button>

            <button type="button" className="btn-link" onClick={() => navigate('/')}>
              {t('common', 'passwordReset.backToLogin')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
